import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import '@adornis/entity-picker/client/x-multi-entity-picker.js';
import { AbstractAspect } from '@adornis/filter/aspect-schemas/AbstractAspect.js';
import { escapeRegex } from '@adornis/filter/aspect-schemas/escapeRegex.js';
import type { AspectConfig } from '@adornis/filter/filter-options.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import '@adornis/popover/x-dropdown-multiselection.js';
import { ExpectedError } from '@adornis/validation/errors/ExpectedError.js';
import { html, nothing } from 'lit';
import { getSchoolYearIntervalDates } from '../../schoolyear/getSchoolYear.js';
import { Grade } from '../entities/ClassBooking.js';

function buildYearFilter(year: string) {
  // the getSchoolYearIntervalDates method already does some string cleaning but as we do not trust users we do some beforehand here anyway
  year = year.trim().replaceAll(' ', '').replaceAll('-', '/').replaceAll('_', '/');

  // the user might only give one year but the method wants two years -> take the next year aswell
  const yearParts = year.split('/');
  if (yearParts.length === 1) {
    const firstYear = yearParts[0];
    if (!firstYear) return null;
    const nextYear = parseInt(firstYear, 10) + 1;
    // check nan
    if (Number.isNaN(nextYear)) return null;
    yearParts.push(nextYear.toString());
  }

  try {
    const { start, end } = getSchoolYearIntervalDates(yearParts.join('/'));
    if (!start || !end) return null;

    // booking runtimes and the school year interval are canonical calendar days — exact bounds
    return [{ startDate: { $gte: start } }, { endDate: { $lte: end } }];
  } catch (e) {
    if (e instanceof Error) {
      throw new ExpectedError(e.message);
    }
    throw new ExpectedError('Unbekannter Fehler');
  }
}

@Entity()
export class ClassBookingFilterAspect extends AbstractAspect {
  static override _class = 'ClassBookingFilterAspect';

  // the _id of the role
  @Field(() => String) grade: Maybe<string>;

  @Field(() => String) classIdentifier: Maybe<string>;

  // in the format 24/25
  @Field(() => String) year: Maybe<string>;

  // only used for displaying in the aspect
  @Field(() => String) name: Maybe<string>;

  constructor(doc: PartialEntityData<ClassBookingFilterAspect>) {
    super(doc);
  }

  override getMongoFilter(): object | null {
    if (!this.fieldName) return null;

    const selectors: Record<string, any>[] = [];

    if (this.grade) {
      selectors.push({ grade: escapeRegex(this.grade) });
    }

    if (this.classIdentifier) {
      selectors.push({ classIdentifier: { $regex: escapeRegex(this.classIdentifier), $options: 'i' } });
    }

    if (this.year) {
      const schoolYearFilters = buildYearFilter(this.year);
      if (schoolYearFilters) selectors.push(...schoolYearFilters);
    }

    if (!selectors.length) return null;

    return { [this.fieldName]: { $elemMatch: { $and: selectors } } };
  }
}

export const ClassBookingFilterAspectSchema: (params?: {
  showTimeSelector?: boolean;
}) => AspectConfig<ClassBookingFilterAspect> = params => ({
  aspectConstructor: ClassBookingFilterAspect,
  renderEditor: ({ aspectValue }) => {
    const constructAspectName = () => {
      if (!aspectValue.grade && !aspectValue.classIdentifier && !aspectValue.year) {
        aspectValue.name = null;
        return;
      }
      aspectValue.name = `${aspectValue.grade || '---'} ${aspectValue.classIdentifier || '---'} ${aspectValue.year || '---'}`;
    };

    const showTimeSelector = params?.showTimeSelector ?? true;

    return html`
      <div class="flex gap-2 min-w-0">
        <div class="flex flex-1 min-w-0">
          ${DropdownSelectionInput({
            clearable: true,
            placeholder: 'Stufe',
            freeText: true,
            placeholderMode: 'hide',
            selectables: Object.values(Grade),
            value: aspectValue.grade as Grade,
            onPicked: value => {
              aspectValue.grade = value;

              constructAspectName();
            },
          })}
        </div>
        <div class="flex flex-1 min-w-0">
          ${TextInput({
            clearable: true,
            placeholder: 'Klassenbezeichner',
            placeholderMode: 'prefix',
            value: aspectValue.classIdentifier,
            onPicked: value => {
              aspectValue.classIdentifier = value;

              constructAspectName();
            },
          })}
        </div>
        ${
          showTimeSelector
            ? html`
                <div class="flex flex-1 min-w-0">
                  ${TextInput({
                    clearable: true,
                    placeholder: 'Jahrgang (bsp: 25/26)',
                    placeholderMode: 'hide',
                    value: aspectValue.year,
                    onPicked: value => {
                      aspectValue.year = value;
                      constructAspectName();
                    },
                  })}
                </div>
              `
            : nothing
        }
      </div>
    `;
  },
  renderPreview: ({ aspectValue }) => aspectValue.name ?? null,
});
